import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X, Lightning, FileText, ChatText, Microphone, Globe,
  MagicWand, CaretRight,
} from '@phosphor-icons/react';

const WORKER_TYPES = [
  {
    id: 'event',
    label: 'Custom Event Worker',
    description: 'Build a custom trigger-based workflow — emails, webhooks, schedules, and more.',
    icon: Lightning,
    color: '#00E5C0',
  },
  {
    id: 'file_analysis',
    label: 'File / Analysis Worker',
    description: 'Upload files for processing — audio, video, PDF, documents. Get scores, insights, reports.',
    icon: FileText,
    color: '#3B82F6',
  },
  {
    id: 'chat',
    label: 'Chat Worker',
    description: 'Conversational assistant that interacts with users through persistent chat.',
    icon: ChatText,
    color: '#8B5CF6',
  },
  {
    id: 'voice',
    label: 'Voice Worker',
    description: 'Real-time voice interaction — phone calls, voice assistants, speech-to-speech.',
    icon: Microphone,
    color: '#EC4899',
  },
  {
    id: 'operator',
    label: 'Operator Worker',
    description: 'System-level execution — browse the web, use tools, manage files, run complex tasks.',
    icon: Globe,
    color: '#EF4444',
    tag: 'Orion',
  },
];

const NewWorkerModal = ({ isOpen, onClose, onVoiceSelect, onOperatorSelect }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSelect = (typeId) => {
    onClose();
    if (typeId === 'voice') {
      if (onVoiceSelect) { onVoiceSelect(); return; }
    }
    if (typeId === 'operator') {
      if (onOperatorSelect) { onOperatorSelect(); return; }
    }
    if (typeId === 'wizard') {
      navigate('/workers/setup');
    } else {
      navigate(`/workers/new?type=${typeId}`);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-[#12161C] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="new-worker-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.08)]">
              <div>
                <h2 className="text-lg font-semibold text-[#F0F4F8]">Create a new worker</h2>
                <p className="text-xs text-[#7D8896] mt-0.5">Choose the type of worker to build.</p>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.06)] text-[#7D8896] hover:text-white transition-colors" data-testid="close-new-worker-modal">
                <X size={18} />
              </button>
            </div>

            {/* Options */}
            <div className="p-4 space-y-2">
              {/* Guided Setup Wizard */}
              <button onClick={() => handleSelect('wizard')} data-testid="modal-worker-type-guided-setup"
                className="w-full flex items-center gap-4 border border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.02)] rounded-xl p-4 text-left hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.04)] transition-all group">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(0,229,192,0.1)]">
                  <MagicWand size={20} className="text-[#00E5C0]" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">Easy Template Setup Wizard</h3>
                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] font-semibold uppercase tracking-wider">Recommended</span>
                  </div>
                  <p className="text-[11px] text-[#7D8896] mt-0.5">Step-by-step guided setup — choose tools, and configure in minutes.</p>
                </div>
                <CaretRight size={14} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors shrink-0" />
              </button>

              {/* Standard workers */}
              {WORKER_TYPES.filter(wt => !['voice', 'operator'].includes(wt.id)).map((wt) => {
                const Icon = wt.icon;
                return (
                  <button key={wt.id} onClick={() => handleSelect(wt.id)} data-testid={`modal-worker-type-${wt.id}`}
                    className="w-full flex items-center gap-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 text-left hover:border-[rgba(0,229,192,0.2)] hover:bg-[rgba(255,255,255,0.03)] transition-all group">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${wt.color}12` }}>
                      <Icon size={20} style={{ color: wt.color }} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">{wt.label}</h3>
                      <p className="text-[11px] text-[#7D8896] mt-0.5">{wt.description}</p>
                    </div>
                    <CaretRight size={14} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors shrink-0" />
                  </button>
                );
              })}

              {/* Divider */}
              <div className="flex items-center gap-3 pt-3 pb-1">
                <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
                <span className="text-[9px] text-[#5C6778] uppercase tracking-widest font-medium">Specialty Workers</span>
                <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
              </div>

              {/* Specialty workers */}
              {WORKER_TYPES.filter(wt => ['voice', 'operator'].includes(wt.id)).map((wt) => {
                const Icon = wt.icon;
                return (
                  <button key={wt.id} onClick={() => handleSelect(wt.id)} data-testid={`modal-worker-type-${wt.id}`}
                    className="w-full flex items-center gap-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 text-left hover:border-[rgba(0,229,192,0.2)] hover:bg-[rgba(255,255,255,0.03)] transition-all group">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${wt.color}12` }}>
                      <Icon size={20} style={{ color: wt.color }} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">{wt.label}</h3>
                        {wt.tag && (
                          <span className="text-[8px] px-1.5 py-0.5 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] font-semibold uppercase tracking-wider">{wt.tag}</span>
                        )}
                      </div>
                      <p className="text-[11px] text-[#7D8896] mt-0.5">{wt.description}</p>
                    </div>
                    <CaretRight size={14} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors shrink-0" />
                  </button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewWorkerModal;
